// @ts-strict-ignore
import React, { useEffect, useMemo, useState } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { isNumber } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { useDebounce } from '@node-space/hooks'
import {
  Avatar,
  Box,
  Button,
  Callout,
  Card,
  DataListItem,
  Input,
  RadioOption,
  RequestError,
} from '@node-space/ui-components'
import { ellipseMiddleText } from '@node-space/utils'
import {
  PayoutsFeeEstimateParams,
  usePayoutsFeeEstimateQuery,
} from 'hooks/queries/usePayoutsFeeEstimateQuery'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import {
  contactAtom,
  contactInstrumentActionAtom,
  contactInstrumentAtom,
} from 'pages/Contacts/atoms/contactAtoms'
import { ContactInstrumentsSheet } from 'pages/Contacts/ContactInstruments'
import {
  getConfirmPayeeBeneficiaryParams,
  mapBankAccountPaymentInstrumentToBeneficiaryDetails,
} from 'pages/Contacts/utils/fiatInstruments'
import { useWalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { ContactInstrumentActionIds } from 'types/atoms/atom.contacts'
import {
  BaseErrorResponse,
  BeneficiaryTransferDestination,
  PaymentMethods,
  USDBeneficiaryOptions,
} from 'types/beneficiaries'
import { ContactPaymentInstruments } from 'types/contacts'
import { ConfirmPayeeBeneficiaryParams } from 'types/payoutsV2'
import { Currencies } from 'types/types'
import { UnifiedPayoutsV2FeeEstimate } from 'types/unifiedPayouts/unifiedPayoutsV2'
import { FiatSendStep, FiatSendSteps } from 'types/walletActions'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import { getBeneficiaryName } from 'utils/beneficiaries'
import track from 'utils/tracker'
import {
  formatString,
  generateReference,
  roundNumberWithCommas,
  stringToDecimal,
} from 'utils/utils'
import {
  fiatSendFormAtom,
  selectedContactBankAccountAtom,
  selectedFiatBeneficiaryAtom,
  selectedWalletAtom,
} from '../../Pay/atoms/atoms'
import { FeeErrors, MAX_GBP_PAYMENT_AMOUNT, SEPA_METHODS } from '../constants'
import { FiatSendFormKeys } from '../form/fiatSendFormSchema'
import { useConfirmBeneficiaryCallout } from '../hooks/useConfirmBeneficiaryCallout'
import { usePaymentMethodLimits } from '../hooks/usePaymentMethodLimits'
import { ConfirmationOfPayeeCalloutTypes } from '../types'
import {
  getUnifiedPayoutRequestV2,
  getUnifiedPayoutRequestV2WithContactBankAccount,
  setPayoutErrors,
} from '../utils'
import { ConfirmationOfPayeeCallout } from './ConfirmationOfPayeeCallout'
import { AchSameDayCheckbox } from './shared/AchSameDayCheckbox'
import { PaymentMethodGroup } from './shared/PaymentMethodGroup'
import { PaymentMethodSelection } from './shared/PaymentMethodSelection'

interface PaymentDetailsProps {
  setStep: React.Dispatch<React.SetStateAction<FiatSendStep>>
}

export const PaymentDetails = ({ setStep }: PaymentDetailsProps) => {
  const { t } = useTranslation()
  const { enableAchSameDayPayments, enableCustomerFee, enablePayoutPaymentMethodSelection } =
    useFeatureFlags()

  const [walletAction] = useWalletActionContext()

  const sourceWalletAtom = useAtomValue(selectedWalletAtom)
  const [beneficiaryAtom, setBeneficiaryAtom] = useAtom(selectedFiatBeneficiaryAtom)
  const contactBankAccountAtom = useAtomValue(selectedContactBankAccountAtom)
  const formAtom = useAtomValue(fiatSendFormAtom)
  const [showErrorState, _setShowErrorState] = useState(false)
  const [feeErrorMessages, setFeeErrorMessages] = useState<string[]>([])

  const { formValues, errors, register, setValue, trigger, setError, clearErrors, formState } =
    formAtom || {}

  const debouncedSendAmount = useDebounce(formValues?.amount, 500)
  const debouncedPaymentReference = useDebounce(formValues?.paymentReference, 500)

  const { isMethodDisabledForAmount } = usePaymentMethodLimits(formValues?.currencyCode)

  const [showContactInstrumentSheet, setShowContactInstrumentSheet] = useState(false)
  const setContact = useSetAtom(contactAtom)
  const setContactInstrument = useSetAtom(contactInstrumentAtom)
  const setContactInstrumentAction = useSetAtom(contactInstrumentActionAtom)

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.FIAT_PAYOUT_DETAILS, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })
  }, [])

  useEffect(() => {
    setValue('twoFactorCode', '')
  }, [])

  useEffect(() => {
    const fallbackPaymentReference = generateReference()

    setValue(
      'paymentReference',
      formValues?.paymentReference ||
        beneficiaryAtom?.beneficiaryPaymentReference ||
        fallbackPaymentReference
    )
  }, [beneficiaryAtom])

  const beneficiaryDetails: ConfirmPayeeBeneficiaryParams = useMemo(() => {
    return getConfirmPayeeBeneficiaryParams(beneficiaryAtom)
  }, [beneficiaryAtom])

  const { confirmPayeeData, confirmationOfPayeeCallout, isConfirmingPayee, editBankAccount } =
    useConfirmBeneficiaryCallout({
      paymentMethods: [formValues.paymentMethod],
      beneficiaryDetails,
      isPayout: true,
    })

  useEffect(() => {
    setValue('beneficiaryVerificationId', confirmPayeeData?.id ?? '')
  }, [confirmPayeeData])

  const request = useMemo(() => {
    return contactBankAccountAtom
      ? getUnifiedPayoutRequestV2WithContactBankAccount({
          formValues,
          selectedWallet: sourceWalletAtom,
          contactPaymentInstrument: contactBankAccountAtom,
        })
      : getUnifiedPayoutRequestV2(formValues, sourceWalletAtom, beneficiaryAtom)
  }, [contactBankAccountAtom, formValues, sourceWalletAtom, beneficiaryAtom])

  const payoutFeeEstimateParams = useMemo((): PayoutsFeeEstimateParams => {
    return {
      request,
      isEnabled: false,
    }
  }, [request])

  const isBenficiaryPaymentMethodACH = useMemo(() => {
    return beneficiaryAtom?.paymentMethod === USDBeneficiaryOptions.ACH
  }, [beneficiaryAtom?.paymentMethod])

  const {
    isFetching: isFetchingFee,
    refetch: fetchFee,
    error: feeError,
  } = usePayoutsFeeEstimateQuery(payoutFeeEstimateParams)

  useEffect(() => {
    setFeeErrorMessages([])
    if (feeError) {
      const feeErrorResponse = feeError as BaseErrorResponse

      if (feeErrorResponse?.data?.code === FeeErrors.BVNK_0000) {
        trigger('amount')
        return
      }

      if (feeErrorResponse?.data?.code === FeeErrors.LIMIT_EXCEEDED) {
        track.Amp.track(AmplitudeEvent.EOF_PAYOUT_LIMIT_EXCEEDED, {
          category: AmplitudeEventCategory.MERCHANT_PORTAL,
          action: AmplitudeEventAction.ERROR,
          step: FiatSendSteps.DETAILS,
          errorCode: feeErrorResponse?.data?.code,
          errorMessage: feeErrorResponse?.data?.message,
        })
      }

      setPayoutErrors({
        feeErrorResponse,
        setError,
        setFeeErrorMessages,
      })

      track.Amp.track(AmplitudeEvent.FIAT_PAYOUT_ERROR, {
        category: AmplitudeEventCategory.MERCHANT_PORTAL,
        action: AmplitudeEventAction.ERROR,
        step: FiatSendSteps.DETAILS,
        errorType: 'FEE_ERROR',
        error: feeErrorResponse,
      })

      return
    }

    clearErrors()
  }, [feeError])

  const walletTitle = useMemo(() => {
    return `${sourceWalletAtom?.walletName} | ${formatString(
      t('balanceWithCurrency'),
      roundNumberWithCommas(sourceWalletAtom?.balance),
      sourceWalletAtom?.currency
    )}`
  }, [sourceWalletAtom])

  const feeText = useMemo(() => {
    if ((!debouncedSendAmount || feeError || !formValues?.fee) && !isFetchingFee) {
      return `0.00 ${sourceWalletAtom?.currency}`
    }

    if (isFetchingFee) {
      return t('wallets.fiatSend.calculatingFee')
    }

    return `${roundNumberWithCommas(formValues?.fee)} ${sourceWalletAtom?.currency}`
  }, [isFetchingFee, formValues?.fee, sourceWalletAtom, debouncedSendAmount, feeError])

  const isPaymentMethodSelectionAvailable = useMemo(() => {
    if (
      !enablePayoutPaymentMethodSelection ||
      beneficiaryAtom?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL
    )
      return false

    switch (formValues?.currencyCode) {
      case Currencies.EUR:
        return true
      case Currencies.GBP:
      case Currencies.USD:
      case Currencies.AED:
      case Currencies.ZAR:
      default:
        return false
    }
  }, [formValues?.currencyCode])

  const paymentMethodOptions: RadioOption[] = useMemo(() => {
    switch (formValues?.currencyCode) {
      case Currencies.EUR:
        return [
          {
            label: `${t('wallets.fiatSend.autoPaymentMethod')} (${t('wallets.fiatSend.recommended')})`,
            value: '',
            secondaryLabel: t('wallets.fiatSend.autoPaymentMethodDescription'),
            disabled: false,
          },
          {
            label: t('wallets.fiatSend.sepaInstant'),
            value: PaymentMethods.SEPA_INSTANT,
            secondaryLabel: t('wallets.fiatSend.sepaInstantDescription'),
            disabled: isMethodDisabledForAmount(PaymentMethods.SEPA_INSTANT, formValues?.amount),
          },
          {
            label: t('wallets.fiatSend.sepaCredit'),
            value: PaymentMethods.SEPA,
            secondaryLabel: t('wallets.fiatSend.sepaCreditDescription'),
            disabled: isMethodDisabledForAmount(PaymentMethods.SEPA, formValues?.amount),
          },
        ]
      case Currencies.GBP:
      case Currencies.USD:
      case Currencies.AED:
      case Currencies.ZAR:
      default:
        return []
    }
  }, [formValues?.currencyCode, formValues?.amount, isMethodDisabledForAmount])

  useEffect(() => {
    const isValidNumberAmount = isNumber(Number(formValues?.amount))
    const selectedMethod = formValues?.paymentMethod
    const isSepaPaymentMethod = !!selectedMethod && SEPA_METHODS.has(selectedMethod)
    const isOverMethodLimit =
      isSepaPaymentMethod && isMethodDisabledForAmount(selectedMethod, formValues?.amount)
    const shouldResetPaymentMethod =
      isValidNumberAmount &&
      isPaymentMethodSelectionAvailable &&
      isSepaPaymentMethod &&
      isOverMethodLimit

    if (shouldResetPaymentMethod) {
      setValue('paymentMethod', '')
    }
  }, [
    formValues?.amount,
    isPaymentMethodSelectionAvailable,
    formValues?.paymentMethod,
    isMethodDisabledForAmount,
  ])

  const onInputChange = (field: FiatSendFormKeys, event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(field, event.target.value)
  }

  const handleOnContinueClick = async () => {
    clearErrors()
    const isValid = await trigger(['amount', 'paymentReference'])

    if (isValid) {
      track.Amp.track(AmplitudeEvent.FIAT_PAYOUT_DETAILS_CONTINUE, {
        category: AmplitudeEventCategory.MERCHANT_PORTAL,
        action: AmplitudeEventAction.CLICK,
        amount: formValues?.amount,
        paymentReference: formValues?.paymentReference,
        fee: formValues.fee,
        walletId: sourceWalletAtom?.walletId,
        beneficiaryReference: beneficiaryAtom?.reference,
        ...(formValues?.paymentMethod && { paymentMethod: formValues.paymentMethod }),
        ...(formValues?.achSameDay && { achSameDay: formValues.achSameDay }),
        ...(formValues?.customerFee && { customerFee: formValues.customerFee }),
      })
      setStep(FiatSendSteps.CONFIRMATION)
    }
  }

  const handleOnCancelClick = () => {
    track.Amp.track(AmplitudeEvent.FIAT_PAYOUT_CANCEL, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.CLICK,
      step: FiatSendSteps.DETAILS,
    })
    walletAction?.onClose()
  }

  const validateAmount = async (amount: string) => {
    setFeeErrorMessages([])
    const error = await checkMaxAmount(amount)

    if (error) {
      setError('amount', { message: error })
    } else {
      clearErrors('amount')
    }
  }

  const checkMaxAmount = async (amount: string) => {
    const customerFeeAmount = Number(formValues?.customerFee ?? 0)
    const total = Number(amount ?? 0) + customerFeeAmount

    const feeData = await fetchFee()
    const { availablePayoutAmount, fees } = (feeData?.data || {}) as UnifiedPayoutsV2FeeEstimate
    const { processingFee } = fees || {}

    const amountErrorMaximumFees =
      processingFee &&
      sourceWalletAtom &&
      formatString(
        t('amountErrorMaximumFees'),
        roundNumberWithCommas(availablePayoutAmount?.amount),
        sourceWalletAtom?.currency
      )

    if (Number(total) > (availablePayoutAmount?.amount ?? 0)) {
      return amountErrorMaximumFees
    }

    setValue('fee', processingFee?.amount?.toString() ?? '')
  }

  const amountOnBlurHandler = async (event: React.FocusEvent<HTMLInputElement, Element>) => {
    const isValid = await trigger('amount')

    if (!isValid) {
      return
    }

    const amount = Number(event?.target?.value ?? 0)
    const customerFeeAmount = Number(formValues?.customerFee ?? 0)
    const total = amount + customerFeeAmount
    const isGbpPayment = sourceWalletAtom?.currency === Currencies.GBP

    if (!Number.isNaN(total) && isGbpPayment && total > MAX_GBP_PAYMENT_AMOUNT) {
      setError('amount', { message: t('wallets.fiatSend.maximumGbpAmount') })
      return
    }

    event?.target?.value && validateAmount(stringToDecimal(event?.target?.value))
  }

  const paymentReferenceOnBlurHandler = (event: React.FocusEvent<HTMLInputElement, Element>) => {
    if (formState?.errors?.paymentReference) {
      return
    }

    if (event?.target?.value) {
      setValue('paymentReference', event?.target?.value)
    }
  }

  useEffect(() => {
    const shouldRefetchFee = !!debouncedSendAmount && !Number.isNaN(Number(debouncedSendAmount))

    if (shouldRefetchFee) {
      validateAmount(debouncedSendAmount)
    }
  }, [
    debouncedSendAmount,
    formValues?.achSameDay,
    formValues?.paymentMethod,
    debouncedPaymentReference,
  ])

  const isContinueButtonDisabled = useMemo(() => {
    return (
      !formValues.amount ||
      !formValues.paymentReference ||
      !!errors?.amount ||
      !!feeErrorMessages?.length
    )
  }, [formValues.amount, formValues.paymentReference, errors?.amount, feeErrorMessages?.length])

  const isCustomerFeeEnabled = useMemo(() => {
    return !!walletAction?.meta?.customerWallet && enableCustomerFee
  }, [walletAction?.meta?.customerWallet])

  const handleBankAccountUpdate = (paymentInstrument: ContactPaymentInstruments) => {
    const beneficiary = mapBankAccountPaymentInstrumentToBeneficiaryDetails(paymentInstrument)
    setValue('beneficiaryReference', beneficiary?.reference)
    setValue('currencyCode', beneficiary?.currency)
    setValue('transferDestination', beneficiary?.transferDestination)
    setBeneficiaryAtom(beneficiary)
  }

  const handleEditBankAccount = () => {
    setContact(contactBankAccountAtom?.contact)
    setContactInstrument(contactBankAccountAtom?.paymentInstrument)
    setContactInstrumentAction({
      actionId: ContactInstrumentActionIds?.FIAT_INSTRUMENT,
      header: t('contacts.updateBankAccount'),
      meta: {
        keepUpdatedPaymentInstrument: true,
        onPaymentInstrumentUpdate: handleBankAccountUpdate,
      },
      onClose: () => {
        setShowContactInstrumentSheet(false)
      },
    })
    setShowContactInstrumentSheet(true)
  }

  return (
    <Box className="max-h-[75vh]">
      {showErrorState && (
        <RequestError
          description={
            <>
              {t('requestErrors.comeBackLater')}{' '}
              <Button variant="ghost" noPadding onClick={() => window.location.reload()}>
                {t('requestErrors.refreshPage')}
              </Button>
            </>
          }
        />
      )}
      {!showErrorState && (
        <Box paddingB={16}>
          <Card variant="static" emphasis full>
            <DataListItem
              id="wallet"
              title={t('wallets.fiatSend.sendingFromWallet')}
              BodyAvatar={<Avatar type="currency" name={sourceWalletAtom?.currency} size="small" />}
              body={walletTitle}
              layout={'vertical'}
            />

            <DataListItem
              id="beneficiary"
              title={t('wallets.fiatSend.sendingTo')}
              BodyAvatar={<Avatar type="entity" name={'A'} size="small" />}
              body={`${getBeneficiaryName(beneficiaryAtom)} | ${ellipseMiddleText(beneficiaryAtom?.fiat?.accountNumber, 6, 6)}`}
              layout={'vertical'}
            />
          </Card>

          {!!feeErrorMessages?.length && (
            <Box paddingT={16}>
              <Callout variant="error">
                {feeErrorMessages?.map((error: string) => (
                  <Box key={`fee-error-${error}`}>{error}</Box>
                ))}
              </Callout>
            </Box>
          )}

          <>
            {confirmationOfPayeeCallout && (
              <ConfirmationOfPayeeCallout
                type={
                  contactBankAccountAtom
                    ? ConfirmationOfPayeeCalloutTypes.BANK_ACCOUNT_PAYOUT
                    : ConfirmationOfPayeeCalloutTypes.BENEFICIARY_PAYOUT
                }
                confirmationOfPayeeCallout={confirmationOfPayeeCallout}
                confirmPayeeData={confirmPayeeData}
                editBankAccount={!!contactBankAccountAtom && editBankAccount}
                handleEditBankAccount={handleEditBankAccount}
              />
            )}

            <Box flexCol paddingY={16} width="full" gapY={16}>
              <Input
                {...register('amount')}
                label="Amount"
                placeholderText={'0'}
                onChange={e => onInputChange('amount', e)}
                testId="fiat-send-amount"
                type="text"
                inputMode="numeric"
                onBlur={amountOnBlurHandler}
                loading={isFetchingFee}
                rightSuffixText={sourceWalletAtom?.currency}
                error={!!errors.amount?.message}
                errorText={errors.amount?.message}
              />

              {isCustomerFeeEnabled && (
                <Input
                  {...register('customerFee')}
                  label={t('wallets.fiatSend.customerFee')}
                  placeholderText={'0'}
                  onChange={e => onInputChange('customerFee', e)}
                  testId="fiat-customer-fee"
                  type="text"
                  inputMode="numeric"
                  optionalText={t('optional')}
                  rightSuffixText={sourceWalletAtom?.currency}
                  error={!!errors.customerFee?.message}
                  errorText={errors.customerFee?.message}
                />
              )}

              <Input
                {...register('paymentReference')}
                label="Payment reference"
                onChange={e => onInputChange('paymentReference', e)}
                onBlur={paymentReferenceOnBlurHandler}
                testId="fiat-send-payment-reference"
                error={!!errors.paymentReference?.message}
                errorText={errors.paymentReference?.message}
              />

              {!contactBankAccountAtom && (
                <>
                  {isPaymentMethodSelectionAvailable && (
                    <PaymentMethodGroup
                      formValues={formValues}
                      paymentMethodOptions={paymentMethodOptions}
                      error={errors?.paymentMethod?.message}
                      setValue={setValue}
                    />
                  )}

                  {isBenficiaryPaymentMethodACH && enableAchSameDayPayments && (
                    <Box paddingT={12} paddingB={8}>
                      <AchSameDayCheckbox
                        formValues={formValues}
                        register={register}
                        setValue={setValue}
                      />
                    </Box>
                  )}
                </>
              )}

              {contactBankAccountAtom && (
                <PaymentMethodSelection
                  formValues={formValues}
                  contactPaymentInstrument={contactBankAccountAtom}
                  currency={formValues?.currencyCode}
                  amount={formValues?.amount}
                  error={errors?.paymentMethod?.message}
                  register={register}
                  setValue={setValue}
                />
              )}

              <DataListItem
                key={feeText}
                id="transaction-fee"
                title={t('wallets.fiatSend.transactionFee')}
                body={feeText}
                layout={'vertical'}
              />
            </Box>

            <Box flex justify="between" paddingT={20}>
              <Button testId="cancel-btn" variant="secondary" onClick={handleOnCancelClick}>
                {t('cancel')}
              </Button>
              <Button
                testId="continue-btn"
                onClick={handleOnContinueClick}
                disabled={isContinueButtonDisabled || isConfirmingPayee}
                loading={isFetchingFee || isConfirmingPayee}
              >
                {t('continue')}
              </Button>
            </Box>
          </>
        </Box>
      )}
      <ContactInstrumentsSheet isSheetOpen={showContactInstrumentSheet} isNestedSheet />
    </Box>
  )
}
